<template>
  <b-card>
    <div>
      <!-- search input -->
      <b-row class="mb-1 align-items-center">
        <!-- Search input -->
        <b-col cols="12" sm="6" lg="4" class="mb-1 mb-sm-0">
          <b-form-input
              v-model.lazy="searchTerm"
              placeholder="Search..."
              type="text"
              class="d-inline-block w-100"
              @keyup="onSearch"
          />
        </b-col>

        <!-- Create button -->
        <b-col
            cols="12"
            sm="6"
            lg="8"
            class="d-flex justify-content-center justify-content-sm-end"
        >
          <template v-if="$permissionAbility(HOURLY_PAYSLIP_POLICY_CREATE, permissions)">
            <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="flex-shrink-0"
                @click="showModal"
            >
              Create
            </b-button>
          </template>
        </b-col>
      </b-row>

      <!-- table -->
      <vue-good-table
        style-class="vgt-table table-custom-style striped"
        :line-numbers="false"
        mode="remote"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :rows="rows"
        :sort-options="{
          enabled: false,
          multipleColumns: true,
          initialSortBy: [
            { field: 'id', type: 'desc' },
            { field: 'created_at', type: 'desc' },
          ],
        }"
        :columns="columns"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
        @on-page-change="onPageChange"
        @on-sort-change="onSortChange"
        @on-column-filter="onColumnFilter"
        @on-per-page-change="onPerPageChange"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <!-- Column: Action -->

          <template v-if="props.column.field === 'format_title'">
            <b
              v-if="$permissionAbility(HOURLY_PAYSLIP_POLICY_SHOW, permissions)"
              class="cursor-pointer"
              @click="onShowPage(props.row)"
            >{{ props?.row?.title }} </b>
            <b v-else>{{ props?.row?.title }} </b>
          </template>

          <template v-if="props.column.field === 'format_duration'">
            <b-badge
              v-if="props.row.duration_in_days"
              variant="success"
            >
              {{ props.row.duration_in_days }}
            </b-badge>
            <template v-else>
              N/A
            </template>
          </template>

          <template v-if="props.column.field === 'format_work_hour'">
            <b-badge
              v-if="props.row.work_hours"
              variant="primary"
            >
              {{ props.row.work_hours }}
            </b-badge>
            <template v-else>
              N/A
            </template>
          </template>

          <template v-if="props.column.field === 'format_start_time'">
            <b v-if="props.row.start_time">
              {{ formatFnTableCheckIn(props.row.start_time) }}
            </b>
            <template v-else>
              N/A
            </template>
          </template>

          <template v-if="props.column.field === 'format_end_time'">
            <b v-if="props.row.end_time">
              {{ formatFnTableCheckIn(props.row.end_time) }}
            </b>
            <template v-else>
              N/A
            </template>
          </template>

          <template v-if="props.column.field === 'format_timer'">
            <b-badge
                v-if="props.row.timer"
                variant="success"
            >
              On
            </b-badge>
            <b-badge
                v-else
                variant="danger"
            >
              Off
            </b-badge>
          </template>

          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="text-body align-middle mr-25"
                  />
                </template>
                <template
                    v-if="$permissionAbility(HOURLY_PAYSLIP_POLICY_EDIT, permissions)"
                >
                  <b-dropdown-item @click="onShow(props.row)">
                    <feather-icon
                        icon="Edit2Icon"
                        class="mr-50"
                    />
                    <span>Edit</span>
                  </b-dropdown-item>
                </template>

                <template
                    v-if="$permissionAbility(HOURLY_PAYSLIP_POLICY_SHOW, permissions)"
                >
                  <b-dropdown-item @click="onShowPage(props.row)">
                    <feather-icon
                        icon="EyeIcon"
                        class="mr-50"
                    />
                    <span>Show</span>
                  </b-dropdown-item>
                </template>

                <template
                    v-if="
                    $permissionAbility(HOURLY_PAYSLIP_POLICY_DELETE, permissions)
                  "
                >
                  <b-dropdown-item @click="onDelete(props.row.id)">
                    <feather-icon
                        icon="TrashIcon"
                        class="mr-50"
                    />
                    <span>Delete</span>
                  </b-dropdown-item>
                </template>
              </b-dropdown>
            </span>
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template
          slot="pagination-bottom"
          slot-scope="props"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                v-model="pageLength"
                :options="['10', '25', '50', '100', '500']"
                class="mx-1"
                @input="
                  (value) => props.perPageChanged({ currentPerPage: value })
                "
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>

    <b-modal
      id="modal-hourly-policy-form"
      centered
      :title="
        modelType == 'editModel'
          ? 'Edit Hourly Work Policy'
          : 'Create Hourly Work Policy'
      "
      hide-footer
      no-close-on-backdrop
      @hidden="hiddenModal"
    >

      <validation-observer ref="policyForm">
        <b-form @submit.prevent="submit">

          <!-- title -->
          <b-form-group
            label="Title"
            label-for="title"
            class="required-label"
          >
            <validation-provider
              #default="{ errors }"
              name="title"
              vid="title"
              rules="required|max:255"
            >
              <b-form-input
                id="title"
                v-model="title"
                type="text"
                :state="errors.length > 0 ? false : null"
                placeholder="Enter Policy Title"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Duration (in days)"
            label-for="duration"
            class="required-label"
          >
            <validation-provider
              #default="{ errors }"
              name="duration"
              vid="duration"
              rules="required|min_value:1"
            >
              <b-form-input
                id="duration"
                v-model="duration"
                type="number"
                :state="errors.length > 0 ? false : null"
                placeholder="1"
              />

              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
              label="Work Hour"
              label-for="work_hour"
              class="required-label"
          >
            <validation-provider
                #default="{ errors }"
                name="work_hour"
                vid="work_hour"
                rules="required|min_value:1"
            >
              <b-form-input
                  id="work_hour"
                  v-model="work_hour"
                  type="number"
                  :state="errors.length > 0 ? false : null"
                  placeholder="1"
              />

              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-checkbox
              id="customSwitchOverTime"
              v-model="timer"
              switch
              class="custom-toggle"
          >
            Timer
          </b-form-checkbox>

          <template v-if="timer">
            <!-- check in-->
            <b-form-group
                label="Check In*"
                label-for="check-in"
            >
              <validation-provider
                  #default="{ errors }"
                  name="check in"
                  vid="check_in"
                  rules="required"
              >
                <flat-pickr
                    id="check-in"
                    v-model="start_time"
                    class="form-control"
                    placeholder="09:00 AM"
                    :config="{
                  enableTime: true,
                  noCalendar: true,
                  static: true,
                  dateFormat: 'H:i:S',
                  altInput: true,
                  altFormat: 'h:i K',
                  minuteIncrement: 1
                }"
                    :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <!-- check out-->
            <b-form-group
                label="Check Out*"
                label-for="check-out"
            >
              <validation-provider
                  #default="{ errors }"
                  name="check out"
                  vid="check_out"
                  rules="required"
              >
                <flat-pickr
                    id="check-out"
                    v-model="end_time"
                    class="form-control"
                    placeholder="06:00 PM"
                    :config="{
                  enableTime: true,
                  noCalendar: true,
                  static: true,
                  dateFormat: 'H:i:S',
                  altInput: true,
                  altFormat: 'h:i K',
                  minuteIncrement: 1
                }"
                    :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </template>

          <!-- loading button -->
          <template v-if="isLoading">
            <b-button
              class="float-right"
              variant="primary"
              disabled
            >
              <b-spinner small />
              Loading...
            </b-button>
          </template>

          <!-- submit button -->
          <template v-else>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              class="float-right"
              variant="primary"
            >
              Submit
            </b-button>
          </template>
        </b-form>
      </validation-observer>
    </b-modal>

  </b-card>
</template>

<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BButton,
  BForm,
  BModal,
  BSpinner,
  BFormDatepicker,
  VBTooltip, BTable, BRow, BCol, BFormCheckbox,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, max } from '@validations'
import { salaryTypeConstants } from '@/helpers/constant/salaryTypeConstant'
import { lateTypeConstants } from '@/helpers/constant/lateTypeConstant'
import { amountValueTypeConstants } from '@/helpers/constant/amountValueTypeConstant'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapGetters } from 'vuex'
import {
  HOURLY_PAYSLIP_POLICY_CREATE,
  HOURLY_PAYSLIP_POLICY_EDIT,
  HOURLY_PAYSLIP_POLICY_DELETE,
  HOURLY_PAYSLIP_POLICY_SHOW,
} from '@/helpers/permissionsConstant'
import flatPickr from 'vue-flatpickr-component'

export default {
  name: 'DelayFinePolicyView',
  components: {
    BFormCheckbox,
    BCol,
    BRow,
    BTable,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BFormDatepicker,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      HOURLY_PAYSLIP_POLICY_CREATE,
      HOURLY_PAYSLIP_POLICY_EDIT,
      HOURLY_PAYSLIP_POLICY_DELETE,
      HOURLY_PAYSLIP_POLICY_SHOW,

      isLoading: false,
      modelType: '',
      title: '',
      work_hour: '',
      duration: '',
      timer: false,
      start_time: null,
      end_time: null,

      pageLength: 10,
      columns: [
        {
          label: 'Title',
          field: 'format_title',
          sortable: false,
        },
        {
          label: 'Work Hour',
          field: 'format_work_hour',
          sortable: false,
        },
        {
          label: 'Duration (in days)',
          field: 'format_duration',
          sortable: false,
        },
        {
          label: 'Timer',
          field: 'format_timer',
          sortable: false,
        },
        {
          label: 'Start Date',
          field: 'format_start_time',
          sortable: false,
          formatFn: this.formatFnTableCheckIn,
        },
        {
          label: 'End Time',
          field: 'format_end_time',
          sortable: false,
          formatFn: this.formatFnTableCheckIn,
        },
        {
          label: 'Action',
          field: 'action',
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: '',
      delayTimer: null,
      isLoading: false,
      isDelayFinePolicy: false,
      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: [
          { field: 'id', type: 'desc' },
          { field: 'created_at', type: 'desc' },
        ],
        page: 1,
        perPage: 10,
      },
    }
  },

  computed: {
    ...mapGetters({
      permissions: 'userModule/getPermissions',
    }),
  },
  methods: {

    onShowPage(value) {
      this.$router.push({
        name: 'admin-payslip-hourly-policy-assign-details',
        params: {
          id: value?.id,
        },
      })
    },

    formatFnTableCheckIn(value) {
      if (value) {
        return this.$moment(value, 'HH:mm:ss').format('LT')
      }
    },

    onContextPurchaseDate(ctx) {
      this.purchaseDate = ctx.selectedYMD
    },
    showModal() {
      this.$bvModal.show('modal-hourly-policy-form')
    },
    hiddenModal() {
      this.modelType = ''
      this.$bvModal.hide('modal-hourly-policy-form')
      this.resetModal()
    },
    resetModal() {
      this.policyId = '';
      this.modelType= '';
      this.title= '';
      this.work_hour= '';
      this.duration= '';
      this.timer= false;
      this.start_time= null;
      this.end_time= null;
    },
    async onShow(value) {
      this.modelType = 'editModel'
      this.policyId = value?.id

      this.title = value?.title
      this.timer = !!value?.timer
      this.work_hour = value?.work_hours
      this.start_time = value?.start_time
      this.end_time = value?.end_time
      this.duration = value?.duration_in_days
      this.showModal()
    },
    async onDelete(id) {
      this.$swal({
        title: 'Warning!',
        text: 'Are You Sure You Want To Delete This?',
        icon: 'warning',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: 'Ok',
        showLoaderOnConfirm: true,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.$api.delete(`/api/hourly-policies/${id}`)

            this.loadItems()

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Success',
                icon: 'BellIcon',
                variant: 'success',
                text: 'Successfully Deleted',
              },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'BellIcon',
                variant: 'danger',
                text: error?.response?.data?.message,
              },
            })
          }
        }
      })
    },

    formatAmount(row) {
      if (row?.amount_value_type == 'percentage') {
        return `${row?.amount_value} %`
      }
      return row?.amount_value
    },
    formatAmountType(value) {
      const amountType = amountValueTypeConstants.find(
        item => item.value === value,
      )
      return amountType?.name
    },
    formatLateType(value) {
      const lateType = lateTypeConstants.find(
        item => item.value === value,
      )
      return lateType?.name
    },

    formatSalaryType(value) {
      const salaryType = salaryTypeConstants.find(
        item => item.value === value,
      )
      return salaryType?.name
    },
    formatDate(value) {
      if (value) {
        return this.$moment(value).format('MMM Do YYYY')
      }
      return 'Not Set'
    },
    onSearch() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer)
        this.delayTimer = null
      }

      this.delayTimer = setTimeout(() => {
        this.loadItems()
      }, 1000)
    },

    updateParams(newProps) {
      this.serverParams = { ...this.serverParams, ...newProps }
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage })
      this.loadItems()
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage })
      this.loadItems()
    },

    onSortChange(params) {
      this.updateParams({
        sort: params,
      })
      this.loadItems()
    },

    onColumnFilter(params) {
      this.updateParams(params)
      this.loadItems()
    },

    async getData(params) {
      return await this.$api.get('api/hourly-policies', {
        params: {
          show: params.show,
          page: params.page,
          sort: params.sort,
          q: params.q,
        },
      })
    },

    async loadItems() {
      try {
        const response = await this.getData({
          show: this.serverParams.perPage,
          page: this.serverParams.page,
          sort: this.serverParams.sort,
          q: this.searchTerm,
        })

        const data = response?.data?.data
        const meta = response?.data?.meta

        this.totalRecords = meta?.pagination?.total
        this.rows = data
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            icon: 'BellIcon',
            variant: 'warning',
            text: error?.response?.data?.message,
          },
        })
      }
    },
    async submit() {
      this.$refs.policyForm.validate().then(async success => {
        if (success) {
          this.isLoading = true
          try {
            const policyData = {
              title: this.title,
              work_hours: this.work_hour,
              duration_in_days: this.duration,
              timer: this.timer,
              start_time: this.start_time,
              end_time: this.end_time,
            }

            if (this.modelType === 'editModel') {
              const response = await this.$api.put(`/api/hourly-policies/${this.policyId}`, policyData)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Success',
                  icon: 'BellIcon',
                  variant: 'success',
                  text: 'Hourly Work Policy Successfully Updated',
                },
              })
            } else {
              const response = await this.$api.post('/api/hourly-policies', policyData)
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Success',
                  icon: 'BellIcon',
                  variant: 'success',
                  text: 'Hourly Work Policy Successfully Created',
                },
              })
            }

            this.loadItems() // Refresh item list
            this.hiddenModal() // Close the modal
            this.isLoading = false
          } catch (error) {
            this.isLoading = false
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Error',
                  icon: 'BellIcon',
                  variant: 'danger',
                  text: error?.response?.data?.message,
                },
              })
            }

            if (error?.response?.data?.errors) {
              this.$refs.addDelayFinePolicy.setErrors(error?.response?.data?.errors)
            }
          }
        }
      })
    },

  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.required-label label::after {
  content: " *";
  color: red;
}
.flatpickr-wrapper{
  width: 100% !important;
}
</style>
